import { Injectable } from '@angular/core';
import {
  Auth,
  authState,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  idToken,
  updateProfile,
  updateEmail,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset
} from '@angular/fire/auth';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Logger } from '../logs/logger.service';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthProvider {
  constructor(
    private auth: Auth,
    private logger: Logger
  ) {}

  /**
   * Get current authenticated Firebase user
   * @returns Observable of current Firebase user or null if not authenticated
   */
  getCurrentAuthUser(): Observable<AuthUser | null> {
    return authState(this.auth).pipe(
      map(firebaseUser => {
        if (!firebaseUser) {
          return null;
        }

        return {
          uid: firebaseUser.uid,
          email: firebaseUser.email || null,
          displayName: firebaseUser.displayName || null,
          photoURL: firebaseUser.photoURL || null,
          emailVerified: firebaseUser.emailVerified
        };
      })
    );
  }

  /**
   * Get current authentication token
   * @returns Observable of current auth token
   */
  getCurrentAuthToken(): Observable<string | null> {
    return idToken(this.auth).pipe(map(token => token || null));
  }

  /**
   * Sign in user with email and password
   * @param email User email
   * @param password User password
   * @returns Promise that resolves with user credentials
   */
  async signIn(email: string, password: string): Promise<AuthUser | null> {
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);

      this.logger.info(`User signed in: ${email}`);

      return {
        uid: credential.user.uid,
        email: credential.user.email,
        displayName: credential.user.displayName,
        photoURL: credential.user.photoURL,
        emailVerified: credential.user.emailVerified
      };
    } catch (error: any) {
      this.logger.error(`Sign in failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Create new user account with email and password
   * @param email User email
   * @param password User password
   * @returns Promise that resolves with new user credentials
   */
  async signUp(email: string, password: string): Promise<AuthUser | null> {
    try {
      const credential = await createUserWithEmailAndPassword(this.auth, email, password);

      this.logger.info(`New user created: ${email}`);

      return {
        uid: credential.user.uid,
        email: credential.user.email,
        displayName: credential.user.displayName,
        photoURL: credential.user.photoURL,
        emailVerified: credential.user.emailVerified
      };
    } catch (error: any) {
      this.logger.error(`Sign up failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Sign in with a server-minted custom token (used to complete the claim flow
   * after the Cloud Function creates the account).
   * @param token Firebase custom auth token
   */
  async signInWithCustomToken(token: string): Promise<AuthUser | null> {
    try {
      const credential = await signInWithCustomToken(this.auth, token);
      this.logger.info('User signed in with custom token');
      return {
        uid: credential.user.uid,
        email: credential.user.email,
        displayName: credential.user.displayName,
        photoURL: credential.user.photoURL,
        emailVerified: credential.user.emailVerified
      };
    } catch (error: any) {
      this.logger.error(`Custom-token sign in failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Sign out current user
   * @returns Promise that resolves when sign out is complete
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(this.auth);
      this.logger.info('User signed out');
    } catch (error: any) {
      this.logger.error(`Sign out failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Send password reset email
   * @param email User email
   * @returns Promise that resolves when email is sent
   */
  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
      this.logger.info(`Password reset email sent to ${email}`);
    } catch (error: any) {
      this.logger.error(`Reset password failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Verify a password-reset oobCode from the email link.
   * @param oobCode One-time code from the reset link query string
   * @returns Promise resolving to the email the code belongs to
   */
  async verifyPasswordResetCode(oobCode: string): Promise<string> {
    try {
      const email = await verifyPasswordResetCode(this.auth, oobCode);
      this.logger.info('Password reset code verified');
      return email;
    } catch (error: any) {
      this.logger.warn(`Verify reset code failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Complete a password reset using the oobCode + new password.
   * @param oobCode One-time code from the reset link query string
   * @param newPassword The user's chosen new password
   */
  async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    try {
      await confirmPasswordReset(this.auth, oobCode, newPassword);
      this.logger.info('Password reset confirmed');
    } catch (error: any) {
      this.logger.error(`Confirm password reset failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update user profile (display name)
   * @param displayName New display name
   * @returns Promise that resolves when update is complete
   */
  async updateProfile(displayName: string): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      await updateProfile(user, { displayName });
      this.logger.info(`Profile updated for ${user.email}`);
    } catch (error: any) {
      this.logger.error(`Update profile failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update user email
   * @param newEmail New email address
   * @returns Promise that resolves when update is complete
   */
  async updateEmail(newEmail: string): Promise<void> {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }

      await updateEmail(user, newEmail);
      this.logger.info(`Email updated for user`);
    } catch (error: any) {
      this.logger.error(`Update email failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Observable listener for auth state changes
   * @returns Observable that emits whenever auth state changes
   */
  onAuthStateChanged(): Observable<AuthUser | null> {
    return this.getCurrentAuthUser();
  }
}
